import React from 'react';
import GlobalLinkIndicator from './components/global-link-indicator';
import NameSnippetMark from './components/name-snippet-mark';
import LinkSnippetMark from 'components/text-editor/components/annotations/components/link-snippet-mark';
import withForwardRef, { ForwardRefProps } from 'hoc/with-forward-ref';
import getTextFromNode from 'components/text-editor/hooks/use-custom-editor/actions/get-text-from-node';
import { useFormikContext } from 'formik';
import { usePrevious } from 'hooks';
import { isUndefined } from 'lodash';
import { CustomBlock } from './style';
import { AnnotationData, AnyObject } from 'types';
/*
    Annotation are divided into two separate kinds; leaves (elements on the text level and used for text decorations, such as underline) and block level elements, which are used for named and linked snippets. By default, block level elements are of type "paragraph" and displayed inside the editor as <p> HTML elements. Custom block elements should not be nested or overlap (this should already be handled automatically).
*/

interface AnnotationProps extends ForwardRefProps<any> {
  element: any;
  children: any;
  text?: string;
  isLink: boolean;
  isNamed: boolean;
  globalLinkCount?: number;
  saveSnippet: (data: AnnotationData, defaultName?: string) => void;
  onGlobalLinkIndicatorClick: Function;
  isGlobalLinkCountVisible: boolean;
  markNodeAsSelected: Function;
  isNote: boolean;
  snippet: any;
  deleteSnippet: Function;
  updateAnnotation: (customData: AnyObject) => void;
}

function Annotation({
  element,
  text,
  children,
  deleteSnippet,
  saveSnippet,
  updateAnnotation,
  isLink,
  isNamed,
  snippet,
  forwardedRef,
  globalLinkCount,
  isGlobalLinkCountVisible,
  onGlobalLinkIndicatorClick,
  ...props
}: AnnotationProps) {
  const previousText = usePrevious(text);
  const { isSubmitting } = useFormikContext();

  const isStartingLink =
    isUndefined(snippet?.id) && (isSubmitting || true) && element.isLink;

  React.useEffect(() => {
    const isSnippet = isNamed || isLink;

    if (isSnippet && previousText && previousText !== text) {
      if (text) {
        saveSnippet({ ...element, text: getTextFromNode(element) });
      } else {
        deleteSnippet();
      }
    }
  }, [text, previousText, isNamed, isLink]);

  React.useEffect(() => {
    if (element.isLink && isLink === false) {
      updateAnnotation({ annotationId: element.annotationId, isLink: false });
    }

    if (element.isNamed && isNamed === false) {
      updateAnnotation({
        annotationId: element.annotationId,
        isNamed: false,
        name: undefined,
      });
    }
  }, []);

  const showLink = isStartingLink || isLink;

  return (
    <CustomBlock
      id={element?.annotationId}
      className={element?.annotationId}
      {...props}
      ref={forwardedRef}
      isSelected={element.isSelected}
      isLink={showLink}
      hasName={isNamed}
      $isNote={props.isNote}>
      {isNamed && <NameSnippetMark isNote={props.isNote} element={element} />}
      {showLink && <LinkSnippetMark element={element} text={text} />}
      {children}
      {Boolean(globalLinkCount) && (
        <GlobalLinkIndicator
          isVisible={isGlobalLinkCountVisible}
          element={element}
          onClick={onGlobalLinkIndicatorClick}
          count={globalLinkCount}
        />
      )}
    </CustomBlock>
  );
}

export default withForwardRef(Annotation);
